import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { useApolloClient } from "react-apollo";
import ReactSelectAsync from "react-select/async";
import { Field, Control, Label } from "rbx";
import { ALL_PRODUCTS_QUERY } from "../../graphql/Products/Products.graphql";
import { ourToast as toast } from "../../utils";

const parseProductForSelect = (product, name) => ({
  value: product.RecId,
  name,
  label: `${product.ProductCode} - ${product.Description}`,
  otherLabel: product.ProductCode,
  description: product.Description
});

const ProductSelectAsync = ({
  label,
  size,
  name,
  onChange,
  disabled,
  value,
  isLoading,
  required,
  where
}) => {
  const client = useApolloClient();
  const [selectLoading, setLoading] = useState(isLoading);
  const [defaultOptions, setDefaultOptions] = useState(null);

  const loadDefaultOptions = useCallback(async () => {
    try {
      setLoading(true);
      const variables = {
        orderByMulti: ["ProductCode_ASC"],
        where
      };

      const productsResult = await client.query({
        query: ALL_PRODUCTS_QUERY,
        variables,
        fetchPolicy: "cache-first"
      });

      const products = productsResult?.data?.Products;

      if (Array.isArray(products)) {
        const grouped = products.map(product =>
          parseProductForSelect(product, name)
        );

        setDefaultOptions(grouped);
      }
    } catch (err) {
      toast.error("Error getting Products.");
    } finally {
      setLoading(false);
    }
  }, [client, name, where]);

  useEffect(() => {
    if (!defaultOptions) {
      loadDefaultOptions();
    }
  }, [defaultOptions, loadDefaultOptions]);

  const loadingOptions = useCallback(
    async (inputValue = "") =>
      new Promise(async resolve => {
        try {
          setLoading(true);
          const variables = {
            orderByMulti: ["ProductCode_ASC"],
            where: {
              ...where,
              OR: [
                { ProductCode_contains: inputValue },
                { Description_contains: inputValue }
              ]
            }
          };

          const productsResult = await client.query({
            query: ALL_PRODUCTS_QUERY,
            variables,
            fetchPolicy: "cache-first"
          });

          const products = productsResult?.data?.Products;
          if (Array.isArray(products)) {
            const grouped = productsResult.data.Products.map(product =>
              parseProductForSelect(product, name)
            );
            resolve(grouped);
          }
        } catch (err) {
          toast.error("Error getting Products.");
        } finally {
          setLoading(false);
        }
      }),
    [client, name, where]
  );
  return (
    <Field>
      <Label size={size}>{label}</Label>
      <Control
        size={size}
        style={{
          border:
            required && (!value || value === "" || value === "0" || value === 0)
              ? "1px #e63362 solid"
              : "",
          borderRadius: "4px",
          boxShadow:
            required && (!value || value === "" || value === "0" || value === 0)
              ? "0 0 0 0.125em rgba(230, 51, 98, 0.25)"
              : ""
        }}
      >
        <ReactSelectAsync
          cacheOptions={false}
          color="danger"
          defaultOptions={defaultOptions}
          isDisabled={disabled}
          isLoading={selectLoading}
          loadOptions={loadingOptions}
          name={name}
          placeholder="Start typing..."
          styles={{
            menu: provided => ({
              ...provided,
              zIndex: 999
            })
          }}
          value={value}
          onChange={onChange}
        />
      </Control>
    </Field>
  );
};

ProductSelectAsync.propTypes = {
  value: PropTypes.object,
  name: PropTypes.string,
  label: PropTypes.string,
  size: PropTypes.string,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  isLoading: PropTypes.bool,
  where: PropTypes.object,
  required: PropTypes.bool
};
ProductSelectAsync.defaultOptions = {
  isLoading: null
};
export default ProductSelectAsync;
